import { changed, rejected, type Command, type CommandExecution } from './commands';
import { damage } from './damage';
import { findLayer, type EditorDocument, type LayerId } from './model';
import { canonicalizeRegions } from './selection';
import { RasterTileStore } from './tiles';
import type { Rect } from './geometry';

export type ApplyImageBlend = 'normal' | 'multiply' | 'screen' | 'add' | 'subtract' | 'darken' | 'lighten';
export type ApplyImageChannel = 'rgb' | 'red' | 'green' | 'blue' | 'alpha' | 'luminosity';

export interface ApplyImageSettings {
  mode: ApplyImageBlend;
  /** Channel taken from operand A (the chosen source layer). */
  channel: ApplyImageChannel;
  opacity: number;
  invert: boolean;
  /** Channel taken from operand B (the blend target). Defaults to RGB. */
  targetChannel?: ApplyImageChannel;
  targetInvert?: boolean;
  /**
   * Photoshop's Add/Subtract divisor and bias: result = (A op B) / scale + offset.
   * Ignored by the other modes, exactly as in Photoshop. Defaults to 1 / 0.
   */
  scale?: number;
  offset?: number;
}

function pixel(store: RasterTileStore, x: number, y: number): readonly [number, number, number, number] {
  if (x < 0 || y < 0 || x >= store.width || y >= store.height) return [0, 0, 0, 0];
  const tileX = Math.floor(x / store.tileSize);
  const tileY = Math.floor(y / store.tileSize);
  const pixels = store.getTile(tileX, tileY);
  if (!pixels) return [0, 0, 0, 0];
  const dimensions = store.tileDimensions(tileX, tileY);
  const index = ((y - tileY * store.tileSize) * dimensions.width + (x - tileX * store.tileSize)) * 4;
  return [pixels[index] ?? 0, pixels[index + 1] ?? 0, pixels[index + 2] ?? 0, pixels[index + 3] ?? 0];
}

export function sourceChannels(source: readonly [number, number, number, number], channel: ApplyImageChannel, invert: boolean): readonly [number, number, number, number] {
  let result: readonly [number, number, number, number];
  if (channel === 'rgb') result = source;
  else {
    const value = channel === 'red' ? source[0]
      : channel === 'green' ? source[1]
        : channel === 'blue' ? source[2]
          : channel === 'alpha' ? source[3]
            : Math.round(source[0] * 0.2126 + source[1] * 0.7152 + source[2] * 0.0722);
    result = [value, value, value, source[3]];
  }
  return invert ? [255 - result[0], 255 - result[1], 255 - result[2], result[3]] : result;
}

function blend(source: number, target: number, mode: ApplyImageBlend): number {
  if (mode === 'multiply') return source * target / 255;
  if (mode === 'screen') return 255 - (255 - source) * (255 - target) / 255;
  if (mode === 'add') return Math.min(255, source + target);
  if (mode === 'subtract') return Math.max(0, target - source);
  if (mode === 'darken') return Math.min(source, target);
  if (mode === 'lighten') return Math.max(source, target);
  return source;
}

function scaled(value: number, mode: ApplyImageBlend, settings: ApplyImageSettings): number {
  if (mode !== 'add' && mode !== 'subtract') return value;
  const divisor = settings.scale === undefined || settings.scale <= 0 ? 1 : settings.scale;
  return Math.max(0, Math.min(255, value / divisor + (settings.offset ?? 0)));
}

/**
 * Blends one prepared source pixel into a target pixel.
 *
 * The target's own alpha is preserved. Apply Image composites into existing
 * pixels; it must not turn transparent areas of the target opaque, which would
 * paint a hard black surround around any cut-out layer.
 */
export function applyImagePixel(source: readonly [number, number, number, number], target: readonly [number, number, number, number], settings: ApplyImageSettings): readonly [number, number, number, number] {
  const prepared = sourceChannels(source, settings.channel, settings.invert);
  const base = sourceChannels(target, settings.targetChannel ?? 'rgb', settings.targetInvert ?? false);
  const amount = Math.max(0, Math.min(1, settings.opacity)) * prepared[3] / 255;
  return [
    Math.round(target[0] + (scaled(blend(prepared[0], base[0], settings.mode), settings.mode, settings) - target[0]) * amount),
    Math.round(target[1] + (scaled(blend(prepared[1], base[1], settings.mode), settings.mode, settings) - target[1]) * amount),
    Math.round(target[2] + (scaled(blend(prepared[2], base[2], settings.mode), settings.mode, settings) - target[2]) * amount),
    target[3],
  ];
}

/**
 * Blends `source` into `target`.
 *
 * `covered` restricts the operation to the active selection, expressed in the
 * target layer's own pixel space. Photoshop clips Apply Image to the selection;
 * without this the command silently rewrites the whole layer when the user has
 * deliberately isolated a region.
 */
export function applyImageStores(source: RasterTileStore, target: RasterTileStore, settings: ApplyImageSettings, covered?: (x: number, y: number) => boolean): RasterTileStore {
  const output = new RasterTileStore(target.width, target.height, target.tileSize);
  const columns = Math.ceil(target.width / target.tileSize);
  const rows = Math.ceil(target.height / target.tileSize);
  for (let tileY = 0; tileY < rows; tileY += 1) for (let tileX = 0; tileX < columns; tileX += 1) {
    // Target alpha is preserved, so a tile the target never populated stays empty.
    if (!target.getTile(tileX, tileY)) continue;
    const dimensions = output.tileDimensions(tileX, tileY);
    const pixels = new Uint8ClampedArray(dimensions.width * dimensions.height * 4);
    let used = false;
    for (let localY = 0; localY < dimensions.height; localY += 1) for (let localX = 0; localX < dimensions.width; localX += 1) {
      const x = tileX * target.tileSize + localX;
      const y = tileY * target.tileSize + localY;
      const sourceX = Math.min(source.width - 1, Math.floor(x / target.width * source.width));
      const sourceY = Math.min(source.height - 1, Math.floor(y / target.height * source.height));
      const original = pixel(source, sourceX, sourceY);
      const existing = pixel(target, x, y);
      const result = covered && !covered(x, y) ? existing : applyImagePixel(original, existing, settings);
      pixels.set(result, (localY * dimensions.width + localX) * 4);
      used ||= result[3] > 0;
    }
    if (used) output.setTile(tileX, tileY, pixels);
  }
  return output;
}

/**
 * Converts a channel of a raster into mask coverage.
 *
 * Masks in this editor are sampled from the alpha byte, so coverage is written
 * to every component. Source transparency scales coverage down: a pixel the
 * source never painted contributes nothing.
 */
export function grayscaleStore(source: RasterTileStore, channel: ApplyImageChannel, invert: boolean, width = source.width, height = source.height): RasterTileStore {
  const output = new RasterTileStore(width, height, source.tileSize);
  const columns = Math.ceil(width / output.tileSize);
  const rows = Math.ceil(height / output.tileSize);
  for (let tileY = 0; tileY < rows; tileY += 1) for (let tileX = 0; tileX < columns; tileX += 1) {
    const dimensions = output.tileDimensions(tileX, tileY);
    const pixels = new Uint8ClampedArray(dimensions.width * dimensions.height * 4);
    let used = false;
    for (let localY = 0; localY < dimensions.height; localY += 1) for (let localX = 0; localX < dimensions.width; localX += 1) {
      const x = tileX * output.tileSize + localX;
      const y = tileY * output.tileSize + localY;
      const sourceX = Math.min(source.width - 1, Math.floor(x / width * source.width));
      const sourceY = Math.min(source.height - 1, Math.floor(y / height * source.height));
      const sample = pixel(source, sourceX, sourceY);
      const prepared = sourceChannels(sample, channel, invert);
      const coverage = Math.round(prepared[0] * prepared[3] / 255);
      const offset = (localY * dimensions.width + localX) * 4;
      pixels[offset] = coverage;
      pixels[offset + 1] = coverage;
      pixels[offset + 2] = coverage;
      pixels[offset + 3] = coverage;
      used ||= coverage > 0;
    }
    if (used) output.setTile(tileX, tileY, pixels);
  }
  return output;
}

/**
 * Renders the blend result at preview resolution by nearest-sampling both
 * operands. Bounded by the requested preview size, so it stays cheap enough to
 * recompute on every control change.
 */
export function applyImagePreview(
  source: RasterTileStore,
  target: RasterTileStore,
  settings: ApplyImageSettings,
  width: number,
  height: number,
  covered?: (x: number, y: number) => boolean,
): Uint8ClampedArray {
  const output = new Uint8ClampedArray(Math.max(1, width) * Math.max(1, height) * 4);
  for (let y = 0; y < height; y += 1) for (let x = 0; x < width; x += 1) {
    const targetX = Math.min(target.width - 1, Math.floor((x + 0.5) / width * target.width));
    const targetY = Math.min(target.height - 1, Math.floor((y + 0.5) / height * target.height));
    const sourceX = Math.min(source.width - 1, Math.floor((x + 0.5) / width * source.width));
    const sourceY = Math.min(source.height - 1, Math.floor((y + 0.5) / height * source.height));
    const existing = pixel(target, targetX, targetY);
    const result = covered && !covered(targetX, targetY) ? existing : applyImagePixel(pixel(source, sourceX, sourceY), existing, settings);
    output.set(result, (y * width + x) * 4);
  }
  return output;
}

export function thresholdRegions(source: RasterTileStore, channel: ApplyImageChannel, invert: boolean, threshold = 128): Rect[] {
  const regions: Rect[] = [];
  for (let y = 0; y < source.height; y += 1) {
    let start: number | null = null;
    for (let x = 0; x <= source.width; x += 1) {
      // A transparent pixel has no tone to threshold. Without this, a cut-out
      // layer's empty surround is selected wherever its tile happens to exist.
      const sample = x < source.width ? pixel(source, x, y) : null;
      const selected = sample !== null && sample[3] > 0 && sourceChannels(sample, channel, invert)[0] >= threshold;
      if (selected && start === null) start = x;
      if (!selected && start !== null) {
        regions.push({ x: start, y, width: x - start, height: 1 });
        start = null;
      }
    }
  }
  return canonicalizeRegions(regions);
}

export class ReplaceMaskPixelsCommand implements Command {
  readonly label = 'Apply Image to mask';
  private before?: RasterTileStore;
  private beforeDefaultAlpha?: 0 | 255;

  constructor(private readonly layerId: LayerId, private readonly after: RasterTileStore) {}
  retainedBytes(): number { return 256 + this.after.byteLength() + (this.before?.byteLength() ?? 0); }
  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (!layer?.mask) return rejected('The active layer has no mask.');
    this.before ??= layer.mask.tiles;
    this.beforeDefaultAlpha ??= layer.mask.defaultAlpha;
    layer.mask.tiles = this.after;
    layer.mask.defaultAlpha = 0;
    return changed(this.damage());
  }
  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.mask && this.before) {
      layer.mask.tiles = this.before;
      layer.mask.defaultAlpha = this.beforeDefaultAlpha ?? 255;
    }
  }
  redo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.mask) {
      layer.mask.tiles = this.after;
      layer.mask.defaultAlpha = 0;
    }
  }
  damage() { return damage({ layerIds: [this.layerId], fullDocument: true }); }
}
